import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
} from "react-icons/fa";

import {
  ReceiptIndianRupee,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const CSTReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "HO";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    storedBranchType || "HO"
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    formNo,
    setFormNo,
  ] = useState("");

  const [
    status,
    setStatus,
  ] = useState("");

  const [
    supplier,
    setSupplier,
  ] = useState("");

  /* =========================================================
     DROPDOWN DATA
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    supplierList,
    setSupplierList,
  ] = useState([]);

  /* =========================================================
     RESULT DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD DROPDOWNS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/dropdown_cstRpt",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              "8",

            branchtype:
              branchType ||
              storedBranchType ||
              "HO",
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const stores =
          res.data.branches ||
          [];

        const suppliers =
          res.data.suppliers ||
          [];

        setBranchList(
          stores
        );

        setSupplierList(
          suppliers
        );

        if (
          !branchId &&
          stores.length > 0
        ) {
          setBranchId(
            stores[0]
              .fld_Branchid
              ?.toString() ||
              storedBranchId ||
              "8"
          );
        }
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchType,
    branchId,
    storedBranchId,
    storedBranchType,
    commonHeaders,
  ]);

  /* =========================================================
     FORMAT DATE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatDate = (
    dateStr
  ) => {
    if (!dateStr) {
      return "";
    }

    const [
      year,
      month,
      day,
    ] = dateStr.split("-");

    return `${day}/${month}/${year}`;
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      from:
        formatDate(
          fromDate
        ) || "",

      to:
        formatDate(
          toDate
        ) || "",

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      cformno:
        formNo ||
        "%",

      cstcformstatus:
        status ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/cstList_rpt",
        {
          params:
            payload,

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data.records ||
          res.data.data ||
          res.data.rows ||
          res.data.recordset ||
          res.data
            .recordsets?.[0] ||
          [];

        setData(rows);

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "CST Report fetch failed",
          err
        );

        toast.error(
          "Failed to fetch CST Report"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     EXPORT EXCEL
     EXISTING REAL FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExcelDownload = () => {
    if (
      !fromDate ||
      !toDate
    ) {
      toast.error(
        "Please select date range"
      );

      return;
    }

    const payload = {
      from:
        formatDate(
          fromDate
        ),

      to:
        formatDate(
          toDate
        ),

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      cformno:
        formNo ||
        "%",

      cstcformstatus:
        status ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/cstList_rpt_excel",
        {
          params:
            payload,

          headers:
            commonHeaders,

          responseType:
            "blob",
        }
      )
      .then((res) => {
        const url =
          window.URL.createObjectURL(
            new Blob([
              res.data,
            ])
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          url;

        link.setAttribute(
          "download",
          `CST_Report_${fromDate}_to_${toDate}.xlsx`
        );

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        window.URL.revokeObjectURL(
          url
        );

        toast.success(
          "Excel file downloaded successfully"
        );
      })
      .catch((err) => {
        console.error(
          "Excel download failed",
          err
        );

        toast.error(
          "Failed to download Excel"
        );
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const payload = {
      from:
        formatDate(
          fromDate
        ) || "",

      to:
        formatDate(
          toDate
        ) || "",

      branch:
        branchId ||
        storedBranchId ||
        "8",

      supplier:
        supplier ||
        "%",

      cformno:
        formNo ||
        "%",

      cstcformstatus:
        status ||
        "%",
    };

    const query =
      new URLSearchParams(
        payload
      ).toString();

    window.open(
      `/inventory/CSTReport_Print?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setFormNo("");
    setStatus("");
    setSupplier("");

    setBranchId(
      storedBranchId ||
        "8"
    );

    setData([]);
  };

  /* =========================================================
     TOTALS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const totalCSTAmount =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            item
          ) =>
            sum +
            (parseFloat(
              item.CSTAmount
            ) ||
              0),
          0
        ),
      [data]
    );

  const totalAccAmount =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            item
          ) =>
            sum +
            (parseFloat(
              item.AccAmt
            ) ||
              0),
          0
        ),
      [data]
    );

  /* =========================================================
     FORMAT NUMBER
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return parseFloat(
      value || 0
    ).toLocaleString(
      "en-IN",
      {
        maximumFractionDigits:
          2,
      }
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            CST Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            MATCHES FIRST PAGE DESIGN
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ReceiptIndianRupee className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  CST Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and manage CST forms and transactions
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT EXCEL */}

              <button
                type="button"
                onClick={
                  handleExcelDownload
                }
                disabled={
                  data.length === 0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaFileExcel
                  size={14}
                />

                Export Excel
              </button>

              {/* SHOW / HIDE */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUPPLIER */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Supplier
                  </label>

                  <select
                    value={
                      supplier
                    }
                    onChange={(e) =>
                      setSupplier(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Supplier
                    </option>

                    {supplierList.map(
                      (s) => (
                        <option
                          key={
                            s.fld_supplierid
                          }
                          value={
                            s.fld_supplierid
                          }
                        >
                          {
                            s.fld_suppliername
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* C FORM NO */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    C.Form No
                  </label>

                  <input
                    type="text"
                    value={
                      formNo
                    }
                    onChange={(e) =>
                      setFormNo(
                        e.target.value
                      )
                    }
                    placeholder="Enter C.Form No"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* STATUS */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Status
                  </label>

                  <select
                    value={
                      status
                    }
                    onChange={(e) =>
                      setStatus(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Status
                    </option>

                    <option value="1">
                      Issued
                    </option>

                    <option value="0">
                      Pending
                    </option>
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION ROW
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  CST Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading CST records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* TABLE HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      GRN No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Inv No
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      CST %
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Acc. Amount
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      CST Amount
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Branch
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      C.Form No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      C.Form Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>
                  </tr>
                </thead>

                {/* TABLE BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* S NO */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.GRNDate ||
                            "-"}
                        </td>

                        {/* SUPPLIER */}

                        <td className="px-4 py-3 font-medium">
                          {item.fld_Suppliername ||
                            "-"}
                        </td>

                        {/* GRN */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.Billno ||
                              "-"}
                          </span>
                        </td>

                        {/* INV NO */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_invno ||
                            "-"}
                        </td>

                        {/* CST % */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {item.cst ?? "-"}
                        </td>

                        {/* ACC AMOUNT */}

                        <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                          ₹{" "}
                          {formatNumber(
                            item.AccAmt
                          )}
                        </td>

                        {/* CST AMOUNT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          ₹{" "}
                          {formatNumber(
                            item.CSTAmount
                          )}
                        </td>

                        {/* BRANCH */}

                        <td className="px-4 py-3">
                          {item.fld_branchname ||
                            "-"}
                        </td>

                        {/* C FORM */}

                        <td className="px-4 py-3">
                          {item.fld_cformno ||
                            "-"}
                        </td>

                        {/* C FORM DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_cformdate ||
                            "-"}
                        </td>

                        {/* STATUS */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                              String(
                                item.status
                              )
                                .toLowerCase()
                                .includes(
                                  "issued"
                                )
                                ? isDark
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-800"
                                : isDark
                                ? "bg-yellow-900/30 text-yellow-300"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {item.status ||
                              "-"}
                          </span>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

                {/* =========================================
                    TOTAL FOOTER
                ========================================= */}

                <tfoot
                  className={
                    isDark
                      ? "bg-slate-900/70"
                      : "bg-slate-100"
                  }
                >
                  <tr>

                    <td
                      colSpan="6"
                      className={`px-4 py-3 text-right text-sm font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Total
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold">
                      ₹{" "}
                      {formatNumber(
                        totalAccAmount
                      )}
                    </td>

                    <td
                      className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                        isDark
                          ? "text-green-300"
                          : "text-green-700"
                      }`}
                    >
                      ₹{" "}
                      {formatNumber(
                        totalCSTAmount
                      )}
                    </td>

                    <td colSpan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBox
                size={42}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No CST records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {/* ===================================================
            SUMMARY
            EXISTING VALUES PRESERVED
        =================================================== */}

        {data.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >

            {/* SUMMARY HEADER */}

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>

                  <h3 className="text-xl font-bold">
                    Summary
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    CST transaction summary
                  </p>
                </div>
              </div>
            </div>

            {/* SUMMARY GRID */}

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                {/* RECORD COUNT */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Records
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold text-${baseColor}-${baseShade}`}
                  >
                    {data.length}
                  </p>
                </div>

                {/* ACCOUNT AMOUNT */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Account Amount
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      totalAccAmount
                    )}
                  </p>
                </div>

                {/* CST AMOUNT */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total CST Amount
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-blue-300"
                        : "text-blue-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      totalCSTAmount
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default CSTReport;